import * as React from "react"
import compose from "recompose/compose"
import defaultProps from "recompose/defaultProps"
import injectSheet from "react-jss"
import cn from "classnames"
import { color } from "@xingternal/360-styles/helpers"
import { Text } from "@xingternal/360-components"
import { A } from "@xingternal/rebranding-adapter"
import { Box } from "@xingternal/winery-ui/lib/Box"
import { Icon } from "@xingternal/winery-ui/lib/Icon"
import { Infolayer } from "@xingternal/winery-ui/lib/Infolayer"
import {
  ComparisonToDateRange,
  ComparisonToBenchmark,
  KpiValue,
  MiniLineChart,
  FindOutMore,
} from "@/components"
import { getProduct } from "@/helpers/products"
import { formatTypes } from "@/helpers"

const FENCED_OPACITY = 0.3

const styles = {
  kpiContainer: {
    composes: "px24 py12 flex flex-wrap flex-auto justify-between",
    flexFlow: ({ direction }) => direction !== "column" && "column",
  },
  title: {
    composes: "mb8",
    flex: ({ direction }) => direction === "column" && 1,
    order: ({ direction }) => direction === "column" && 2,
    minWidth: ({ direction }) => direction === "column" && "calc(100% - 150px)",
    opacity: ({ fenced }) => fenced && FENCED_OPACITY,
  },
  fencedTextContainer: {
    composes: "flex flex-column flex-auto justify-end rounded",
    flexGrow: ({ direction }) => (direction === "row" ? 0 : 1),
    background: ({ availableProduct }) => availableProduct && color("gray-5"),
    padding: ({ availableProduct }) => availableProduct && "8px 12px",
    marginTop: ({ availableProduct }) => availableProduct && 8,
  },
  fencedText: {
    composes: "tp13 mb8",
    color: ({ availableProduct }) => !availableProduct && color("gray-50"),
  },
  kpiHead: {
    width: ({ direction, chartData }) =>
      direction === "column" && chartData && "50%",
    flex: ({ direction }) => direction === "row" && "1 1 auto",
    opacity: ({ disabled }) => disabled && FENCED_OPACITY,
  },
  valueComparisonContainer: {
    composes: "flex mb12",
    flex: ({ direction }) => (direction === "row" ? "0 1 auto" : "1 1 auto"),
    order: ({ direction }) => direction === "column" && 0,
    minWidth: ({ direction }) => direction === "column" && "50%",
    opacity: ({ fenced }) => fenced && FENCED_OPACITY,
  },
  value: { composes: "mr8" },
  fencedAction: { justifySelf: "end" },
  benchmarkComparison: {
    composes: "mt16",
    paddingBottom: ({ direction }) => direction === "column" && 7,
    opacity: ({ fenced }) => fenced && FENCED_OPACITY,
    marginBottom: ({ availableProduct }) => availableProduct && 16,
    minHeight: 18,
  },
  chart: {
    opacity: ({ fenced }) => fenced && FENCED_OPACITY,
  },
  benchmark: {
    composes: "flex items-center mb8 gray-50 regular",
  },
  stats: {
    width: ({ direction }) => direction === "column" && "50%",
    flex: ({ direction }) => direction === "row" && "0 1 auto",
    paddingLeft: ({ direction }) => direction === "column" && 24,
  },
}

const enhance = compose(
  defaultProps({
    total: 0,
    value: 0,
    formatter: formatTypes["number"],
  }),
  injectSheet(styles),
)

const Fencing = injectSheet(styles)(
  ({ classes, text: FencedText, onClick }) => (
    <div className={classes.fencedTextContainer}>
      <Text className={classes.fencedText}>
        {typeof FencedText === "string"
          ? FencedText
          : FencedText && <FencedText />}
      </Text>
      <FindOutMore className={classes.fencedAction} onClick={onClick} />
    </div>
  ),
)

const KpiOverview = ({
  benchmark,
  chartData,
  classes,
  className,
  companyIds,
  comparisonValue,
  description,
  communityUrl,
  direction,
  fenced,
  fencedText,
  openLightbox,
  formatter,
  icon,
  id,
  name,
  reverseComparison,
  status,
  title,
  value,
  product,
  availableProduct,
  t,
}) => (
  <div className={cn(classes.kpiContainer, className)} data-testid={id}>
    <div className={classes.kpiHead}>
      <div className={classes.valueComparisonContainer}>
        <KpiValue
          className={classes.value}
          comparisonValue={comparisonValue}
          formatter={formatter}
          icon={icon}
          name={name}
          reverseComparison={reverseComparison}
          status={status}
          value={value}
        />
        {comparisonValue !== undefined &&
          comparisonValue !== null &&
          value !== null && (
            <ComparisonToDateRange
              comparisonValue={comparisonValue}
              formatter={formatter}
              status={status}
              value={value}
            />
          )}
      </div>
      <Text className={classes.title} size={13} color={color("gray-80")}>
        {title}
        {description && (
          <Infolayer
            content={
              <Box width={310} m={-2}>
                {description}
                {communityUrl && (
                  <Box mt={2}>
                    <A href={communityUrl} target="_blank">
                      {t("360_RECRUITER_INSIGHTS_TOOLTIP_COMMUNITY_LINK")}
                    </A>
                  </Box>
                )}
              </Box>
            }
            portalContainer={document.body}
            zIndex={5}
          >
            <Icon
              name="CoreHelp"
              width={12}
              height={12}
              color="grey600"
              ml={1}
              top="2px"
              position="relative"
            />
          </Infolayer>
        )}
      </Text>
    </div>
    <div className={classes.stats}>
      {(fenced || availableProduct || chartData || benchmark) && (
        <>
          {chartData && (
            <div className={classes.chart}>
              <MiniLineChart
                data={chartData}
                tooltipFormatter={formatter}
                title={title}
                product={product}
                height={direction === "column" ? 30 : undefined}
              />
            </div>
          )}
          <ComparisonToBenchmark
            className={classes.benchmarkComparison}
            companyIds={companyIds}
            fenced={fenced}
            formatter={formatter}
            id={id}
            reverseComparison={reverseComparison}
            kpiValueStatus={status}
            value={value}
            visible={benchmark}
          />
          {((fenced && fencedText) || availableProduct) && (
            <Fencing
              availableProduct={availableProduct}
              text={
                availableProduct
                  ? t("360_RECRUITER_INSIGHTS_ACCESS", {
                      name: getProduct(product).title,
                    })
                  : fencedText
              }
              onClick={openLightbox}
            />
          )}
        </>
      )}
    </div>
  </div>
)

export const EnhancedKpiOverview = enhance(KpiOverview)
export const StyledKpiOverview = injectSheet(styles)(KpiOverview)
export default KpiOverview
